import React, { useState, useEffect } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../services/api";
import "./Blog.css";

const BlogEditor = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { blogId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (blogId) {
      const fetchBlogContent = async () => {
        try {
          const response = await api.get(`/blogs/${blogId}`);
          const blog = response.data;

          setTitle(blog.title);
          setContent(blog.content || "");
        } catch (err) {
          console.error("Error fetching blog:", err);
          setError("Failed to fetch blog content");
        }
      };
      fetchBlogContent();
    }
  }, [blogId]);

  const handleEditorChange = (newContent) => {
    setContent(newContent);
  };

  const handleImageUpload = (blobInfo, progress) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("file", blobInfo.blob(), blobInfo.filename());

      api
        .post("/upload/upload-image", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.lengthComputable) {
              progress((e.loaded / e.total) * 100);
            }
          },
        })
        .then((res) => {
          if (res.status === 200 && res.data?.url) {
            resolve(res.data.url);
          } else {
            reject("Invalid server response");
          }
        })
        .catch((err) => {
          reject("Upload failed: " + err.message);
        });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (blogId) {
        const response = await api.put(`/blogs/${blogId}`, {
          title,
          content,
        });
        if (response.status === 200) {
          Swal.fire("Success", "Your blog has been updated!", "success").then(
            () => navigate("/blogs")
          );
        }
      } else {
        const response = await api.post("/blogs", { title, content });
        if (response.status === 201) {
          Swal.fire("Success", "Your blog has been published!", "success").then(
            () => navigate("/blogs")
          );
        }
      }
    } catch (err) {
      console.error("Error submitting blog:", err);
      Swal.fire(
        "Error",
        err.response?.data?.message || "Something went wrong. Try again.",
        "error"
      );
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="error-message">{error}</p>;

  return (
    <div className="blog-editor-container">
      <h2>{blogId ? "Edit Blog" : "Add Blog"}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="title">Blog Title</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div className="editor-container">
          <Editor
            apiKey="zog2697k144y1b0sor1yo90ej2x3aobg9mlr5f4dmzgkt65h"
            value={content}
            onEditorChange={handleEditorChange}
            init={{
              height: 500,
              menubar: false,
              plugins: [
                "advlist",
                "autolink",
                "lists",
                "link",
                "image",
                "charmap",
                "preview",
                "anchor",
                "searchreplace",
                "visualblocks",
                "code",
                "fullscreen",
                "insertdatetime",
                "media",
                "table",
                "paste",
                "help",
                "wordcount",
              ],

              toolbar:
                "undo redo | bold italic | alignleft aligncenter alignright | code preview | image",
              images_upload_handler: handleImageUpload,
            }}
          />
        </div>

        <div className="preview-container">
          <h3>Preview</h3>
          <div
            className="blog-preview"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        </div>

        <button type="submit" className="btn-submit">
          {blogId ? "Update Blog" : "Add Blog"}
        </button>
      </form>
    </div>
  );
};

export default BlogEditor;
